import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { LAYER_ID_TO_DRAW_ON } from './constants';
import { useMap } from './useMap';
import { useMapStyle } from './useMapStyle';
import { getBeforeIdToUse } from './utils';

function deepCompareEquals(a, b) {
  return (
    a &&
    b &&
    a.length === b.length &&
    a.sort().every((value, index) => value === b.sort()[index])
  );
}

function useDeepCompareMemoize(value) {
  const ref = useRef();

  if (!deepCompareEquals(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function Raster({
  id,
  tiles,
  tileSize,
  maxZoom,
  minZoom,
  opacity,
  beforeId,
  loadingFinished,
  loadingStarted
}) {
  const map = useMap();
  const mapStyle = useMapStyle();
  const isMountedRef = useRef(true);
  const loaderTimerId = useRef(null);
  const isSourceDataLoaded = useRef(false);
  const sourceName = `${id}-source`;

  const layer = {
    id,
    type: 'raster',
    source: sourceName,
    paint: {
      'raster-opacity': opacity
    }
  };

  const addLayer = () => {
    if (!map.getSource(sourceName)) {
      map.addSource(sourceName, {
        type: 'raster',
        tiles,
        tileSize,
        maxzoom: maxZoom,
        minzoom: minZoom
      });

      const beforeIdToUse = getBeforeIdToUse(map, beforeId);
      map.addLayer(layer, beforeIdToUse);
    }
  };

  useEffect(() => {
    if (!isMountedRef.current && map.getLayer(id)) {
      const beforeIdToUse = getBeforeIdToUse(map, beforeId);
      map.moveLayer(id, beforeIdToUse);
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beforeId]);

  useEffect(() => {
    if (!isMountedRef.current) {
      addLayer();
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle]);

  useEffect(() => {
    if (!isMountedRef.current) {
      try {
        map.removeLayer(layer.id);
        map.removeSource(sourceName);
      } catch {
        // ignore error
        //
        // There could be an error in rare occasions when changing map style and
        // enabling terrain layer almost simultaneously, this is not something a
        // user should come by that often, but as it's not too hard reproduce and
        // if this error happens app/page will crash so we want to prevent this crash
        // so users can continue using the app/page
      }
      addLayer();
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, useDeepCompareMemoize(tiles));

  useEffect(() => {
    if (!isMountedRef.current) {
      map.setPaintProperty(id, 'raster-opacity', opacity);
    }
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opacity]);

  useEffect(() => {
    const handleIdle = () => {
      // eslint-disable-next-line no-use-before-define
      map.off('sourcedataloading', handleSourceDataLoading);
      map.off('idle', handleIdle);
      clearTimeout(loaderTimerId.current);
      loaderTimerId.current = null;
      isSourceDataLoaded.current = true;
      loadingFinished();
    };

    const handleSourceDataLoading = (e) => {
      if (
        !isSourceDataLoaded.current &&
        !e.isSourceDataLoaded &&
        e.sourceId === sourceName
      ) {
        clearTimeout(loaderTimerId.current);
        loaderTimerId.current = setTimeout(() => {
          loadingStarted();
        }, 600);

        map.on('idle', handleIdle);
      }
    };

    map.on('sourcedataloading', handleSourceDataLoading);

    addLayer();

    isMountedRef.current = false;

    return function cleanup() {
      clearTimeout(loaderTimerId.current);
      loaderTimerId.current = null;
      isSourceDataLoaded.current = false;

      map.off('sourcedataloading', handleSourceDataLoading);

      if (map && map.style && map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
        map.removeSource(sourceName);
      }
    };
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

Raster.defaultProps = {
  tileSize: 256,
  maxZoom: 20,
  minZoom: 0,
  opacity: 1.0,
  beforeId: LAYER_ID_TO_DRAW_ON,
  loadingStarted: () => {},
  loadingFinished: () => {}
};

Raster.propTypes = {
  id: PropTypes.string.isRequired,
  tiles: PropTypes.arrayOf(PropTypes.string).isRequired,
  tileSize: PropTypes.number,
  maxZoom: PropTypes.number,
  minZoom: PropTypes.number,
  opacity: PropTypes.number,
  beforeId: PropTypes.string,
  loadingStarted: PropTypes.func,
  loadingFinished: PropTypes.func
};

export default Raster;
